/**
 * 应用外壳与路由装配（T01）。
 *
 * 结构（全部按 390x844 逻辑坐标绝对定位，坐标即安卓移植清单坐标）：
 *   app.frame               手机竖屏画布（PhoneFrame）
 *   app.safeArea.top/.bottom、app.homeIndicator
 *   app.content             页面内容区（0,24,390,744）
 *   app.nav                 底部导航条（0,768,390,56）
 *   app.nav.<route>         4 个导航按钮（88x44）
 *
 * 只做外壳与路由：4 个页面本身是空占位页，业务由后续任务实现。
 */
import { useEffect, useRef } from 'react';
import { recoverBackgroundChats } from '../features/chat/backgroundChat';
import { interruptStaleImageJobs } from '../features/chat/imageJobs';
import { loadFeatureFlags } from '../core/featureFlags';
import { loadEntitlements } from '../features/subscription/entitlements';
import { runDueAutomation } from '../features/automation/scheduler';
import { startMomentSync } from '../features/moments/cloudSync';
import { SkinProvider } from '../features/appearance/SkinProvider';
import type { MutableRefObject, ReactElement } from 'react';

import { AppIcon, IconButton, buildComponentMeta, useComponentMeta } from '../ui';
import type { ComponentMetaInput } from '../ui';
import styles from './App.module.css';
import { FrameBox } from './FrameBox';
import {
  APP_RETURN_META,
  APP_FEATURE_TITLE_META,
  APP_SKIN_BACKDROP_META,
} from './contextNavMeta';
import { PhoneFrame } from './PhoneFrame';
import {
  useNavigation,
  navigate,
  returnNavigation,
  isStandalonePath,
  STANDALONE_TITLES,
} from './hashRoute';
import { HashRouter } from './router';
import { ROUTES } from './routes';
import type { RouteDefinition } from './routes';

/** 内容区：顶部状态栏之下、底部导航条之上。 */
const CONTENT_META: ComponentMetaInput = {
  id: 'app.content',
  page: 'global',
  role: 'container',
  label: '页面内容区',
  layout: { x: 0, y: 24, width: 390, height: 744 },
  zIndex: 2,
  notes: '4 个页面共用的容器；安卓用 FragmentContainerView',
};

/** 底部导航条。 */
const NAV_BAR_META: ComponentMetaInput = {
  id: 'app.nav',
  page: 'global',
  role: 'nav',
  label: '底部导航条',
  layout: { x: 0, y: 768, width: 390, height: 56 },
  zIndex: 10,
  notes: '安卓用 BottomNavigationView，4 个 item 见 app.nav.*',
};

const NAV_ITEM_SIZE = { width: 88, height: 44 } as const;
const NAV_ITEM_Y = 774;
const NAV_ITEM_START_X = 19;

/** 第 index 个导航按钮的逻辑布局（4 个按钮等宽，左右各留 19px）。 */
function navItemLayout(index: number): { x: number; y: number; width: number; height: number } {
  return {
    x: NAV_ITEM_START_X + index * NAV_ITEM_SIZE.width,
    y: NAV_ITEM_Y,
    ...NAV_ITEM_SIZE,
  };
}

interface NavItemProps {
  route: RouteDefinition;
  index: number;
  frameRef: MutableRefObject<HTMLDivElement | null>;
  active: boolean;
}

function NavItem({ route, index, frameRef, active }: NavItemProps): ReactElement {
  const meta: ComponentMetaInput = {
    id: `app.nav.${route.id}`,
    page: 'global',
    role: 'button',
    label: route.label,
    layout: navItemLayout(index),
    zIndex: 20,
    props: ['active'],
    notes: `底部导航 → ${route.title}；安卓用 BottomNavigationView 的 item`,
  };

  return (
    <IconButton
      meta={meta}
      frameRef={frameRef}
      className={active ? `${styles.navItem} ${styles.navItemActive}` : styles.navItem}
      aria-current={active ? 'page' : undefined}
      onClick={() => {
        navigate(route.path);
      }}
      icon={
        <span className={styles.navIcon}>
          <AppIcon name={route.icon} size={21} className={styles.navGlyph} />
          <span className={styles.navLabel}>{route.label}</span>
        </span>
      }
    />
  );
}

function SkinBackdrop({ clear }: { clear: boolean }): ReactElement {
  const componentMeta = buildComponentMeta(APP_SKIN_BACKDROP_META, 'background');
  const ref = useComponentMeta<HTMLDivElement>(componentMeta);
  return (
    <div
      ref={ref}
      className={styles.skinBackdrop}
      data-ui-id={componentMeta.id}
      data-ui-role={componentMeta.role}
      data-clear={clear ? 'true' : 'false'}
      aria-hidden="true"
    />
  );
}

export function App(): ReactElement {
  useEffect(() => {
    const stopMomentSync = startMomentSync();
    const recover = (): void => { void recoverBackgroundChats(); };
    void loadFeatureFlags();
    void loadEntitlements();
    void runDueAutomation();
    void interruptStaleImageJobs().catch(() => undefined);
    recover();
    const automate = (): void => { void runDueAutomation(); };
    window.addEventListener('focus', recover); window.addEventListener('online', recover);
    window.addEventListener('focus', automate);
    const timer = window.setInterval(recover, 15000);
    const automationTimer = window.setInterval(automate, 30000);
    return () => {
      stopMomentSync();
      clearInterval(timer); clearInterval(automationTimer);
      window.removeEventListener('focus', recover); window.removeEventListener('online', recover);
      window.removeEventListener('focus', automate);
    };
  }, []);
  const frameRef = useRef<HTMLDivElement | null>(null);
  const { path: activePath } = useNavigation();
  const standaloneTitle = isStandalonePath(activePath) ? STANDALONE_TITLES[activePath] : null;
  const fullScreenAdmin = activePath === '/admin';
  const clearWallpaper = activePath === '/chat';

  return (
    <SkinProvider>
    {fullScreenAdmin ? <div className={styles.adminViewport} data-testid="admin-viewport"><HashRouter /></div> : (
    <PhoneFrame frameRef={frameRef}>
      <SkinBackdrop clear={clearWallpaper} />
      <FrameBox meta={CONTENT_META} className={styles.content}>
        <HashRouter />
      </FrameBox>
      <FrameBox meta={NAV_BAR_META} className={styles.navBar} surface="panel" />
      {standaloneTitle ? (
        <>
          <IconButton
            meta={APP_RETURN_META}
            frameRef={frameRef}
            className={styles.navItem}
            onClick={returnNavigation}
            title="返回"
            icon={
              <AppIcon name="back" size={24} />
            }
          />
          <FrameBox
            meta={APP_FEATURE_TITLE_META}
            className={styles.featureTitle}
          >
            {standaloneTitle}
          </FrameBox>
        </>
      ) : (
        ROUTES.map((route, index) => (
          <NavItem
            key={route.path}
            route={route}
            index={index}
            frameRef={frameRef}
            active={route.path === activePath}
          />
        ))
      )}
    </PhoneFrame>
    )}
    </SkinProvider>
  );
}
